/**
 * 🪝 React Hook для работы с аналитическими данными
 * 
 * Этот хук служит мостиком между независимым сервисом аналитики 
 * и React компонентами, используя Observer Pattern
 */

import { useState, useEffect } from "react"
import { debugLog } from '@/lib/debug'
import { 
  analyticsService, 
  type AnalyticsData, 
  type AnalyticsObserver 
} from "@/lib/analytics-service"

/**
 * 🎯 Хук для получения и отслеживания данных аналитики
 */
export function useAnalyticsData() {
  // Локальное состояние React (только для отображения)
  const [data, setData] = useState<AnalyticsData>(() => analyticsService.getData())

  useEffect(() => {
    debugLog('api', '🪝 [useAnalyticsData] Подписываемся на изменения')

    // Создаем наблюдателя (Observer)
    const observer: AnalyticsObserver = {
      onDataChanged: (newData: AnalyticsData) => {
        debugLog('api', '🔄 [useAnalyticsData] Получены новые данные:', newData)
        setData(newData) // Обновляем React состояние
      }
    }

    // Подписываемся на изменения в сервисе
    const unsubscribe = analyticsService.subscribe(observer)

    // Получаем актуальные данные при подписке
    setData(analyticsService.getData())

    // Первичную загрузку делает страница /analytics (useEffect), чтобы не дублировать loadAnalytics

    // Cleanup: отписываемся при размонтировании
    return () => {
      debugLog('api', '🧹 [useAnalyticsData] Отписываемся от изменений')
      unsubscribe()
    }
  }, [])

  // Возвращаем данные и методы управления
  return {
    // 📊 Данные
    data,
    
    // 🎛️ Методы управления
    loadAnalytics: () => analyticsService.loadAnalytics(),
    refresh: () => analyticsService.refresh(),
    clear: () => analyticsService.clear(),
    
    // 📈 Статистика сервиса
    getStats: () => analyticsService.getStats(),
    
    // 🎯 Прямой доступ к сервису (если нужно)
    service: analyticsService
  }
}

/**
 * 🎛️ Хук только для управления (без данных)
 * Полезен для компонентов, которые только управляют, но не отображают данные
 */
export function useAnalyticsActions() {
  return {
    loadAnalytics: () => analyticsService.loadAnalytics(),
    refresh: () => analyticsService.refresh(),
    clear: () => analyticsService.clear(),
    getStats: () => analyticsService.getStats(),
    service: analyticsService
  }
} 